import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/AdminLayout';
import { Plus, Eye, EyeOff, Edit, Loader2, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminTemplates = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState('en'); // en, ru, kz
  
  const [formData, setFormData] = useState({
    name: { en: '', ru: '', kz: '' },
    occasion_title: { en: '', ru: '', kz: '' },
    occasion_title_font_size: 36,
    wish_text: { en: '', ru: '', kz: '' },
    is_visible: true
  });

  useEffect(() => {
    const isAuth = localStorage.getItem('adminAuth');
    if (!isAuth) {
      navigate('/admin');
      return;
    }
    fetchTemplates();
  }, [navigate]);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/admin/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingTemplate(null);
    setFormData({
      name: { en: '', ru: '', kz: '' },
      occasion_title: { en: '', ru: '', kz: '' },
      occasion_title_font_size: 36,
      wish_text: { en: '', ru: '', kz: '' },
      background_image_url: '',
      is_visible: true
    });
    setActiveTab('en');
    setShowModal(true);
  };

  const openEditModal = (template) => {
    setEditingTemplate(template);
    
    // Convert to multi-language format if needed
    const nameDict = typeof template.name === 'string' 
      ? { en: template.name, ru: '', kz: '' }
      : template.name;
    const occasionDict = typeof template.occasion_title === 'string'
      ? { en: template.occasion_title, ru: '', kz: '' }
      : template.occasion_title;
    const wishDict = typeof template.wish_text === 'string'
      ? { en: template.wish_text, ru: '', kz: '' }
      : template.wish_text;
    
    setFormData({
      name: nameDict,
      occasion_title: occasionDict,
      occasion_title_font_size: template.occasion_title_font_size || 36,
      wish_text: wishDict,
      is_visible: template.is_visible
    });
    setActiveTab('en');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingTemplate) {
        await axios.put(`${API}/admin/templates/${editingTemplate.id}`, formData);
      } else {
        await axios.post(`${API}/admin/templates`, formData);
      }
      
      setShowModal(false);
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template');
    }
  };

  const toggleVisibility = async (templateId, currentVisibility) => {
    try {
      await axios.put(`${API}/admin/templates/${templateId}/visibility`, {
        is_visible: !currentVisibility
      });
      fetchTemplates();
    } catch (error) {
      console.error('Error toggling visibility:', error);
    }
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-black">Gift Card Templates</h1>
          <button
            onClick={openCreateModal}
            className="px-4 py-2 bg-[#1E88E5] text-white rounded-lg hover:bg-[#1565C0] transition-colors flex items-center space-x-2"
            data-testid="create-template-btn"
          >
            <Plus className="w-5 h-5" />
            <span>Create Template</span>
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-12 h-12 text-[#1E88E5] animate-spin" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => (
              <div
                key={template.id}
                className="bg-white rounded-lg shadow-md overflow-hidden border-2 border-gray-200"
              >
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-8">
                  <div className="text-center">
                    <h3 className="text-3xl font-bold mb-3 text-gray-800">
                      {typeof template.occasion_title === 'string' ? template.occasion_title : template.occasion_title?.en || ''}
                    </h3>
                    <p className="text-base text-gray-600 italic">
                      {typeof template.wish_text === 'string' ? template.wish_text : template.wish_text?.en || ''}
                    </p>
                  </div>
                  <div className="absolute top-2 right-2">
                    {template.is_visible ? (
                      <span className="px-2 py-1 bg-green-500 text-white text-xs rounded-full">
                        Visible
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-gray-500 text-white text-xs rounded-full">
                        Hidden
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-4">
                  <h4 className="text-lg font-semibold text-black mb-3">
                    {typeof template.name === 'string' ? template.name : template.name?.en || 'Unnamed'}
                  </h4>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => toggleVisibility(template.id, template.is_visible)}
                      className={`flex-1 px-3 py-2 rounded-lg transition-colors flex items-center justify-center space-x-1 ${
                        template.is_visible
                          ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                      data-testid={`toggle-visibility-${template.id}`}
                    >
                      {template.is_visible ? (
                        <>
                          <EyeOff className="w-4 h-4" />
                          <span className="text-sm">Hide</span>
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4" />
                          <span className="text-sm">Show</span>
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => openEditModal(template)}
                      className="flex-1 px-3 py-2 bg-[#E3F2FD] text-[#1E88E5] rounded-lg hover:bg-[#1E88E5] hover:text-white transition-colors flex items-center justify-center space-x-1"
                    >
                      <Edit className="w-4 h-4" />
                      <span className="text-sm">Edit</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-black">
                    {editingTemplate ? 'Edit Template' : 'Create New Template'}
                  </h2>
                  <button
                    onClick={() => setShowModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Language Tabs */}
                <div className="flex space-x-2 mb-6 border-b border-gray-200">
                  <button
                    type="button"
                    onClick={() => setActiveTab('en')}
                    className={`px-4 py-2 font-semibold transition-colors border-b-2 ${
                      activeTab === 'en' 
                        ? 'text-[#1E88E5] border-[#1E88E5]' 
                        : 'text-gray-500 border-transparent hover:text-gray-700'
                    }`}
                  >
                    English
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('ru')}
                    className={`px-4 py-2 font-semibold transition-colors border-b-2 ${
                      activeTab === 'ru' 
                        ? 'text-[#1E88E5] border-[#1E88E5]' 
                        : 'text-gray-500 border-transparent hover:text-gray-700'
                    }`}
                  >
                    Русский
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('kz')}
                    className={`px-4 py-2 font-semibold transition-colors border-b-2 ${
                      activeTab === 'kz' 
                        ? 'text-[#1E88E5] border-[#1E88E5]' 
                        : 'text-gray-500 border-transparent hover:text-gray-700'
                    }`}
                  >
                    Қазақша
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-black mb-2">
                      Template Name * ({activeTab.toUpperCase()})
                    </label>
                    <input
                      type="text"
                      value={formData.name[activeTab] || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        name: { ...formData.name, [activeTab]: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                      placeholder={activeTab === 'en' ? "e.g., Birthday" : activeTab === 'ru' ? "например, День рождения" : "мысалы, Туған күн"}
                      required={activeTab === 'en'}
                      data-testid="template-name-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-black mb-2">
                      Occasion Title * ({activeTab.toUpperCase()})
                    </label>
                    <input
                      type="text"
                      value={formData.occasion_title[activeTab] || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        occasion_title: { ...formData.occasion_title, [activeTab]: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                      placeholder={activeTab === 'en' ? "e.g., Happy Birthday!" : activeTab === 'ru' ? "например, С Днём Рождения!" : "мысалы, Туған күніңізбен!"}
                      required={activeTab === 'en'}
                      data-testid="template-occasion-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-black mb-2">
                      Occasion Title Font Size (pixels)
                    </label>
                    <input
                      type="number"
                      min="16"
                      max="72"
                      value={formData.occasion_title_font_size || 36}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        occasion_title_font_size: parseInt(e.target.value) || 36
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                      placeholder="36"
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      Recommended: 24-48px. Default is 36px.
                    </p>
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded">
                      <p className="text-sm font-semibold" style={{ fontSize: `${formData.occasion_title_font_size || 36}px` }}>
                        Preview: {formData.occasion_title[activeTab] || 'Sample Text'}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-black mb-2">
                      Wish Text * ({activeTab.toUpperCase()})
                    </label>
                    <textarea
                      value={formData.wish_text[activeTab] || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        wish_text: { ...formData.wish_text, [activeTab]: e.target.value }
                      })}
                      rows="3"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                      placeholder={activeTab === 'en' ? "e.g., Wishing you joy and happiness!" : activeTab === 'ru' ? "например, Желаем вам радости и счастья!" : "мысалы, Сізге қуаныш пен бақыт тілейміз!"}
                      required={activeTab === 'en'}
                      data-testid="template-wish-input"
                    />
                  </div>

                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="is_visible"
                      checked={formData.is_visible}
                      onChange={(e) => setFormData({ ...formData, is_visible: e.target.checked })}
                      className="w-4 h-4 text-[#1E88E5] border-gray-300 rounded focus:ring-[#1E88E5]"
                      data-testid="template-visible-checkbox"
                    />
                    <label htmlFor="is_visible" className="ml-2 text-sm text-gray-700">
                      Make this template visible to customers
                    </label>
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-[#1E88E5] text-white rounded-lg hover:bg-[#1565C0] transition-colors"
                      data-testid="save-template-btn"
                    >
                      {editingTemplate ? 'Save Changes' : 'Create Template'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminTemplates;
